// Offline ping outbox.
//
// When recordLocationPing fails (no cell signal, captive portal, Supabase
// unreachable), the ping is saved to IndexedDB instead of dropped. When the
// network returns we drain the queue in batches via recordLocationPingsBatch.
//
// This is what lets a phone keep recording a satellite-GPS route across the
// Gobi with zero cell coverage, then sync the whole route once it's back in
// range. Pure JS/TS — OTA-safe, no native/Swift changes.

import { recordLocationPing, recordLocationPingsBatch } from "@/lib/media.functions";
import { isSupabaseReachable } from "./network";
import { logTrackingTimestamp, logQueueOrderOk } from "@/lib/time/timezone-monitor";
import { isSessionMissingError, refreshSessionAndSync } from "@/lib/offline/offline-auth";

/** Friendlier copy for auth-class errors so the user doesn't see raw gotrue strings. */
function friendlyAuthError(msg: string): string {
  if (/auth session missing|jwt|not signed in|refresh|invalid_grant/i.test(msg)) {
    return "Reconnecting… try again in a moment.";
  }
  return msg;
}

export type QueuedPing = {
  tripId: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  capturedAt: string; // ISO
  source: "foreground" | "background" | "manual" | "manual_gps" | "native_ios_background";
  /**
   * Optional deterministic idempotency key. When present, the server upserts
   * with `onConflict: client_point_key, ignoreDuplicates: true`, so racing
   * drains can never produce duplicate rows. Also used to dedupe within the
   * IndexedDB outbox before send.
   */
  clientPointKey?: string;
};

type StoredPing = QueuedPing & { id?: number };

export type OutboxStats = {
  queued: number;
  lastQueuedAt: string | null;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  draining: boolean;
  /** True when the last sync failed because the Supabase session is gone. */
  needsLogin: boolean;
};

const DB_NAME = "roava-ping-outbox";
const STORE = "pings";
const DB_VERSION = 1;
const LS_LAST_SYNC = "roava.pingOutbox.lastSyncAt";
const LS_LAST_ERR = "roava.pingOutbox.lastSyncError";
const LS_NEEDS_LOGIN = "roava.pingOutbox.needsLogin";

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDB(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
  return dbPromise;
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => Promise<T> | T): Promise<T | null> {
  const db = await openDB();
  if (!db) return null;
  return new Promise<T | null>((resolve) => {
    const t = db.transaction(STORE, mode);
    const s = t.objectStore(STORE);
    Promise.resolve(fn(s)).then(
      (v) => { t.oncomplete = () => resolve(v); },
      () => resolve(null),
    );
    t.onerror = () => resolve(null);
    t.onabort = () => resolve(null);
  });
}

// In-memory mirror of count + lastQueuedAt for synchronous reads from React.
const state: OutboxStats = {
  queued: 0,
  lastQueuedAt: null,
  lastSyncAt: null,
  lastSyncError: null,
  draining: false,
  needsLogin: false,
};
// Tracks when the draining flag was set so a user-initiated retry can recover
// from a stuck flag (e.g. a hung fetch that never rejected).
let drainingSince: number | null = null;

const listeners = new Set<() => void>();
// Cached snapshot — same reference returned to React until `notify()` mints a
// new one. Without this, `useSyncExternalStore` saw a new object on every call
// and looped (React #185 "Maximum update depth exceeded").
let cachedSnapshot: OutboxStats = { ...state };
function bumpSnapshot() { cachedSnapshot = { ...state }; }
function notify() {
  bumpSnapshot();
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

export function subscribeOutbox(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getOutboxStats(): OutboxStats {
  return cachedSnapshot;
}

function loadPersistedMeta() {
  if (typeof localStorage === "undefined") return;
  state.lastSyncAt = localStorage.getItem(LS_LAST_SYNC);
  state.lastSyncError = localStorage.getItem(LS_LAST_ERR);
  state.needsLogin = localStorage.getItem(LS_NEEDS_LOGIN) === "1";
}
loadPersistedMeta();
bumpSnapshot();

function setNeedsLogin(on: boolean) {
  state.needsLogin = on;
  if (typeof localStorage !== "undefined") {
    if (on) localStorage.setItem(LS_NEEDS_LOGIN, "1");
    else localStorage.removeItem(LS_NEEDS_LOGIN);
  }
}

/** Called by the auth recovery flow once a fresh session is back. */
export function clearNeedsLogin(): void {
  if (!state.needsLogin) return;
  setNeedsLogin(false);
  setSyncResult(null);
  notify();
}

async function refreshCount() {
  const c = await tx("readonly", (s) => new Promise<number>((resolve) => {
    const r = s.count();
    r.onsuccess = () => resolve(r.result || 0);
    r.onerror = () => resolve(0);
  }));
  state.queued = c ?? 0;
  notify();
}
// Initialise count on module load.
void refreshCount();

export async function enqueuePing(p: QueuedPing): Promise<void> {
  logTrackingTimestamp(p.capturedAt);
  // Idempotency: if this ping carries a clientPointKey, skip the insert when
  // an identical-keyed ping is already queued. Protects against parallel
  // native-drain races that would otherwise enqueue the same point N times
  // before any of them mark it delivered.
  if (p.clientPointKey) {
    const exists = await tx("readonly", (s) => new Promise<boolean>((resolve) => {
      const r = s.openCursor();
      r.onsuccess = () => {
        const cur = r.result;
        if (!cur) return resolve(false);
        const v = cur.value as StoredPing;
        if (v && v.clientPointKey === p.clientPointKey) return resolve(true);
        cur.continue();
      };
      r.onerror = () => resolve(false);
    }));
    if (exists) {
      // eslint-disable-next-line no-console
      console.info(`[ping-outbox] enqueue skip — clientPointKey already queued: ${p.clientPointKey}`);
      return;
    }
  }
  await tx("readwrite", (s) => new Promise<void>((resolve) => {
    const req = s.add(p satisfies StoredPing);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  }));
  state.queued += 1;
  state.lastQueuedAt = p.capturedAt;
  notify();
}

async function readBatch(limit: number): Promise<StoredPing[]> {
  const out: StoredPing[] = [];
  await tx("readonly", (s) => new Promise<void>((resolve) => {
    const req = s.openCursor();
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur || out.length >= limit) return resolve();
      out.push(cur.value as StoredPing);
      cur.continue();
    };
    req.onerror = () => resolve();
  }));
  return out;
}

async function deleteIds(ids: number[]): Promise<void> {
  if (!ids.length) return;
  await tx("readwrite", (s) => new Promise<void>((resolve) => {
    let remaining = ids.length;
    for (const id of ids) {
      const r = s.delete(id);
      r.onsuccess = r.onerror = () => { if (--remaining <= 0) resolve(); };
    }
  }));
}

function setSyncResult(error: string | null) {
  const now = new Date().toISOString();
  if (!error) state.lastSyncAt = now;
  state.lastSyncError = error;
  if (typeof localStorage !== "undefined") {
    if (!error) localStorage.setItem(LS_LAST_SYNC, now);
    if (error) localStorage.setItem(LS_LAST_ERR, error);
    else localStorage.removeItem(LS_LAST_ERR);
  }
}

export type DrainResult = {
  synced: number;
  status: "ok" | "empty" | "busy" | "unreachable" | "error" | "needs_login";
  error?: string;
};

/**
 * Drain the outbox. Safe to call repeatedly — re-entrancy guard prevents
 * overlapping drains. No-op when the queue is empty or Supabase is unreachable.
 * Returns a result describing what happened so user-initiated callers can show
 * feedback. Background callers can ignore the return.
 *
 * `userInitiated: true` bypasses the conservative reachability probe and
 * clears a stale `draining` flag older than 20s (recovers from a hung fetch
 * after Airplane Mode toggles).
 */
export async function drainPingOutbox(
  opts?: { skipReachabilityCheck?: boolean; userInitiated?: boolean },
): Promise<DrainResult> {
  const userInitiated = opts?.userInitiated === true;

  if (state.draining) {
    // Recover from a stuck flag if the user is asking and it's been a while.
    if (userInitiated && drainingSince != null && Date.now() - drainingSince > 20_000) {
      state.draining = false;
      drainingSince = null;
    } else {
      return { synced: 0, status: "busy" };
    }
  }
  // If a previous drain landed in needs-login state, don't keep retrying.
  // The /offline-control card surfaces a "Sign in to resume sync" CTA that
  // clears the flag via clearNeedsLogin() once the session is back.
  if (state.needsLogin) return { synced: 0, status: "needs_login", error: state.lastSyncError || "Sign in required" };
  if (state.queued === 0) return { synced: 0, status: "empty" };
  if (!opts?.skipReachabilityCheck && !userInitiated) {
    const reachable = await isSupabaseReachable();
    if (!reachable) return { synced: 0, status: "unreachable" };
  }
  // User-initiated drains: best-effort refresh the session before posting,
  // so a stale access token (common after landing on LTE) doesn't cause the
  // first batch to fail with "Auth session missing!". Network-class refresh
  // failures are logged as `defer` and don't block the drain attempt.
  // Auto drains: only refresh if the cached JWT is within 60s of expiry or
  // already past — cheap, closes the long-offline auth window for the
  // watchdog reconnect path even when caller didn't pass userInitiated.
  if (userInitiated) {
    try { await refreshSessionAndSync(); } catch { /* ignore */ }
  } else {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      const expSec = data.session?.expires_at ?? 0;
      const nowSec = Math.floor(Date.now() / 1000);
      if (expSec && expSec - nowSec < 60) {
        try { await refreshSessionAndSync(); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }
  state.draining = true;
  drainingSince = Date.now();
  notify();
  let synced = 0;
  // First batch on a user-initiated drain is small (50) so a flaky LTE
  // connection can complete at least one round-trip; subsequent batches
  // ramp back to 200. Background drains stay at 200 throughout.
  let batchIndex = 0;
  try {
    while (true) {
      const limit = userInitiated && batchIndex === 0 ? 50 : 200;
      const batch = await readBatch(limit);
      if (!batch.length) break;
      // Sort by capturedAt ascending so server receives pings in capture
      // order even if IndexedDB insertion order diverged (e.g. native iOS
      // background pings draining after later foreground pings).
      batch.sort((a, b) => {
        const ta = Date.parse(a.capturedAt) || 0;
        const tb = Date.parse(b.capturedAt) || 0;
        return ta - tb;
      });
      logQueueOrderOk(batch.length);
      // Pre-send dedupe by clientPointKey (defense in depth — server-side
      // unique index also enforces this). Keep the first occurrence; mark
      // the rest as "to delete" without re-sending. Keyless rows pass through.
      const seenKeys = new Set<string>();
      const toSend: StoredPing[] = [];
      const dropIds: number[] = [];
      for (const row of batch) {
        const k = row.clientPointKey;
        if (k) {
          if (seenKeys.has(k)) {
            if (typeof row.id === "number") dropIds.push(row.id);
            continue;
          }
          seenKeys.add(k);
        }
        toSend.push(row);
      }
      if (dropIds.length > 0) {
        // eslint-disable-next-line no-console
        console.info(`[ping-outbox] drain dedupe — dropping ${dropIds.length} same-key duplicate(s)`);
        await deleteIds(dropIds);
      }
      const pings = toSend.map(({ id: _id, ...rest }) => rest);
      if (pings.length > 0) {
        const sendOnce = () => recordLocationPingsBatch({ data: { pings } });
        try {
          await sendOnce();
        } catch (e: any) {
          const raw: string = e?.message || "Sync failed";
          // Verbose log of the first failure so we can see auth vs network
          // vs bad-row in the device console.
          // eslint-disable-next-line no-console
          console.warn(
            `[ping-outbox] batch #${batchIndex} failed (size=${pings.length}, userInitiated=${userInitiated}): ${raw}`,
            { name: e?.name, status: e?.status, code: e?.code },
          );
          // Session-missing class — stop retrying and surface a login CTA.
          if (isSessionMissingError(e)) {
            const msg = "Sign in required to resume sync.";
            setNeedsLogin(true);
            setSyncResult(msg);
            notify();
            return { synced, status: "needs_login", error: msg };
          }
          // First-failure auto-retry: on user-initiated drains, try once more
          // after a 5s backoff + session refresh. Helps when LTE just came
          // back and the first request lost the race with token refresh.
          if (userInitiated && batchIndex === 0) {
            // eslint-disable-next-line no-console
            console.info("[ping-outbox] retrying first batch in 5s…");
            await new Promise((r) => setTimeout(r, 5000));
            try { await refreshSessionAndSync(); } catch { /* ignore */ }
            try {
              await sendOnce();
            } catch (e2: any) {
              const raw2: string = e2?.message || "Sync failed";
              if (isSessionMissingError(e2)) {
                const msg = "Sign in required to resume sync.";
                setNeedsLogin(true);
                setSyncResult(msg);
                notify();
                return { synced, status: "needs_login", error: msg };
              }
              const msg = friendlyAuthError(raw2);
              // eslint-disable-next-line no-console
              console.warn(`[ping-outbox] retry also failed: ${raw2}`);
              setSyncResult(msg);
              notify();
              return { synced, status: "error", error: msg };
            }
          } else {
            const msg = friendlyAuthError(raw);
            setSyncResult(msg);
            notify();
            return { synced, status: "error", error: msg };
          }
        }
      }
      const ids = toSend.map((b) => b.id).filter((x): x is number => typeof x === "number");

      await deleteIds(ids);
      synced += batch.length;
      state.queued = Math.max(0, state.queued - batch.length);
      batchIndex += 1;
      notify();
    }
    setSyncResult(null);
    setNeedsLogin(false);
  } finally {
    state.draining = false;
    drainingSince = null;
    notify();
  }
  return { synced, status: "ok" };
}

/**
 * Send a ping, falling back to the outbox on any failure. This is the function
 * the active GPS watch should call instead of recordLocationPing directly.
 */
export async function sendOrQueuePing(p: QueuedPing): Promise<"sent" | "queued"> {
  // If there's already a backlog, queue this one too so order is preserved.
  if (state.queued > 0) {
    await enqueuePing(p);
    // Opportunistically try to drain; cheap if unreachable.
    void drainPingOutbox();
    return "queued";
  }
  try {
    await recordLocationPing({ data: p });
    return "sent";
  } catch {
    await enqueuePing(p);
    return "queued";
  }
}
